import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type EtatSignaturesAffaire, type LienSignature } from './api';
import { qrEnSvg } from '../../src/partage/qr';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LES SIGNATURES DE L'AFFAIRE — émettre le lien, montrer le QR, suivre l'état.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur, 24/09/2026 : « il faut aussi une page pour la capture des
 * signatures, cette page est accessible par QR code sans connexion, une page
 * par personne, la page est supprimée par la capture de la signature ».
 *
 * ⚠️⚠️ LE DOS DE LA ROUTE EXISTAIT ET AUCUN ÉCRAN NE L'APPELAIT. C'est la
 * famille de défaut que cette session a passé sa journée à fermer :
 * `api.affaires.monBureau()` était déclarée et n'avait aucun appelant, et le
 * bureau du métier affichait 417 173 contrôles de cohérence sans une affaire.
 * `POST /affaires/:ref/signatures/lien` et `GET .../signatures` marchaient de
 * bout en bout depuis hier, éprouvés contre la production — et le lien ne se
 * rendait que dans une réponse JSON que personne ne voyait.
 *
 * ⚠️ LE QR EST DESSINÉ ICI, PAS DEMANDÉ AU MOTEUR D'IMPRESSION.
 * `doctrust-render` sait faire un QR mais ne rend QUE du PDF : afficher un
 * carré à l'écran passerait par Chromium et un conteneur tiers. `partage/qr.ts`
 * l'encode, et sa matrice est confrontée module par module à une
 * implémentation indépendante (`tests/qr-contre-oracle.test.ts`).
 *
 * ⚠️ L'ADRESSE DU QR VIENT DU SERVICE. La bâtir sur `window.location`
 * porterait l'adresse par laquelle l'AGENT est entré — un `127.0.0.1` de
 * tunnel, l'IP d'un VPN : le téléphone de l'usager n'y arriverait pas, et rien
 * à l'écran ne le dirait. Le QR serait parfait et mènerait nulle part.
 */

interface Signataire {
  partie: string; nom: string; titulaireId: string | null;
  rapproche: string | null; empechement: string | null;
}

/**
 * ⚠️⚠️ LES QUATRE ÉTATS SONT CEUX DE `doc.etat_jeton_signature`, PAS LES MIENS.
 * Première rédaction : j'avais écrit `vivant` et `consomme`. La fonction rend
 * **`a_signer`** et **`signe`**. Conséquence, mesurée en production sur un lien
 * réel : `dejaVivant` était TOUJOURS vide, donc l'écran proposait « Émettre le
 * lien » à côté d'un lien déjà vivant — et émettre RÉVOQUE le précédent. Un
 * agent aurait révoqué sans le savoir le lien qu'il venait de remettre à
 * l'usager, et le QR imprimé serait mort dans sa main.
 *
 * ⚠️ Rien ne pouvait le voir : le repli `?? { classe: 'gris', mot: l.etat }`
 * affichait « a_signer » au lieu d'une phrase, ce qui passe pour un libellé
 * maladroit — pas pour un geste qui détruit.
 */
const MOT_ETAT: Record<string, { classe: string; mot: string }> = {
  a_signer: { classe: 'vert', mot: 'en attente de signature' },
  signe: { classe: 'gris', mot: 'signé' },
  revoque: { classe: 'rouge', mot: 'révoqué' },
  expire: { classe: 'ambre', mot: 'expiré' },
};
/** Le seul état où un lien peut encore servir. */
const VIVANT = 'a_signer';

function Qr({ url, nom }: { url: string; nom: string }) {
  let svg: string;
  try {
    svg = qrEnSvg(url, { titre: `Lien de signature — ${nom}` });
  } catch (e) {
    /* ⚠️ UN QR QUI NE PEUT PAS ÊTRE ENCODÉ SE DIT. Muet, il laisserait un
       carré blanc que l'agent présenterait à l'usager. */
    return <p className="erreur" role="alert">QR impossible : {String(e)}</p>;
  }
  return (
    <div className="qr-signature" aria-label={`Code QR du lien de signature de ${nom}`}
         dangerouslySetInnerHTML={{ __html: svg }} />
  );
}

export function SignaturesAffaire({ reference }: { reference: string }) {
  const [etat, setEtat] = useState<EtatSignaturesAffaire | null>(null);
  const [qui, setQui] = useState<Signataire[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setEtat(await api.affaires.signatures(reference));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
      return;
    }
    try {
      const r = await api.affaires.signataires(reference);
      setQui(r.signataires);
    } catch { setQui(null); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger]);

  async function emettre(s: Signataire) {
    if (!s.titulaireId) return;
    setOccupe(s.partie); setMessage(null); setErreur(null);
    try {
      const r = await api.affaires.emettreLienSignature(reference, {
        partie: s.partie, nomAttendu: s.nom, titulaireId: s.titulaireId,
      });
      setMessage(r.message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    } finally { setOccupe(null); }
  }

  if (erreur && !etat) {
    return (
      <section>
        <h4>Signatures</h4>
        <p className="erreur" role="alert">{erreur}</p>
      </section>
    );
  }
  if (!etat) return <section><p className="attente">Lecture des signatures…</p></section>;

  const dejaVivant = new Set(
    etat.liens.filter((l) => l.etat === VIVANT).map((l) => l.partie));

  return (
    <section className="signatures-affaire">
      <h4>Signatures ({etat.liens.length})</h4>
      <p className="mention">{etat.lecture}</p>
      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ⚠️ L'ADRESSE PUBLIQUE MANQUANTE EST UN EMPÊCHEMENT, PAS UN DÉTAIL :
          sans elle on ne peut construire aucun QR qui mène quelque part. */}
      {!etat.base && (
        <p className="erreur" role="alert">
          L'adresse publique du registre n'est pas configurée (URL_PUBLIQUE) :
          aucun QR n'est affiché, il ne mènerait nulle part.
        </p>
      )}

      {/* ── Émettre ───────────────────────────────────────────────────── */}
      {etat.peutEmettre && qui && qui.length > 0 && (
        <div className="signature-emettre">
          <h5>Faire signer</h5>
          <ul className="liste-fine">
            {qui.map((s) => (
              <li key={s.partie}>
                <strong>{s.nom}</strong>
                <span className="discret"> · {s.partie}</span>
                {/* ⚠️ LE RAPPROCHEMENT PAR LE NOM EST DIT. Deux homonymes se
                    ressemblent trait pour trait, et la signature engagerait
                    le patrimoine de l'autre. */}
                {s.rapproche && <span className="discret"> · rattaché {s.rapproche}</span>}
                {s.empechement ? (
                  <span className="mention empechement">{s.empechement}</span>
                ) : dejaVivant.has(s.partie) ? (
                  <span className="mention">
                    Un lien vivant existe déjà — en émettre un autre révoquerait celui-ci.
                  </span>
                ) : (
                  <button className="bouton discret" disabled={occupe === s.partie}
                          onClick={() => void emettre(s)}>
                    {occupe === s.partie ? 'Émission…' : 'Émettre le lien'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {etat.peutEmettre && qui && qui.length === 0 && (
        <p className="mention">
          Le formulaire de cette affaire ne nomme aucune partie : il n'y a personne à faire signer.
        </p>
      )}

      {/* ── Les liens ─────────────────────────────────────────────────── */}
      {etat.liens.length > 0 && (
        <ul className="liens-signature">
          {etat.liens.map((l: LienSignature, i) => {
            const e = MOT_ETAT[l.etat] ?? { classe: 'gris', mot: l.etat };
            const url = l.jeton && etat.base
              ? `${etat.base.replace(/\/+$/, '')}/signer/${l.jeton}` : null;
            return (
              <li key={`${l.partie}-${l.emis_le}-${i}`} className={`lien-signature ${e.classe}`}>
                <div className="lien-signature-fait">
                  <strong>{l.nom_attendu}</strong>
                  <span className="discret"> · {l.partie} · acte <code>{l.acte}</code></span>
                  <span className={`etat ${e.classe}`}>{e.mot}</span>
                  <span className="mention">
                    Émis le {new Date(l.emis_le).toLocaleString('fr-FR')}
                    {l.etat === VIVANT && ` · expire le ${new Date(l.expire_le).toLocaleDateString('fr-FR')}`}
                    {/* ⚠️ LE NOMBRE D'OUVERTURES EST DIT. Un lien ouvert dix
                        fois et jamais signé n'est pas un lien oublié : c'est
                        quelqu'un qui n'arrive pas à s'en servir. */}
                    {l.consultations > 0 && ` · ouvert ${l.consultations} fois`}
                    {l.consomme_le && ` · signé le ${new Date(l.consomme_le).toLocaleString('fr-FR')}`}
                    {l.revoque_le && ` · révoqué — ${l.motif_revocation ?? 'sans motif'}`}
                  </span>
                </div>
                {url && (
                  <div className="lien-signature-qr">
                    <Qr url={url} nom={l.nom_attendu} />
                    {/* ⚠️ L'ADRESSE EST ÉCRITE SOUS LE QR. Un téléphone qui ne
                        scanne pas — écran fissuré, caméra sale — laisse
                        l'usager sans recours si le lien n'est pas lisible. */}
                    <code className="qr-adresse">{url}</code>
                    <button className="bouton discret"
                            onClick={() => { void navigator.clipboard?.writeText(url); }}>
                      Copier l'adresse
                    </button>
                  </div>
                )}
                {/* ⚠️ « pas de QR » a deux causes opposées, et on les sépare. */}
                {!url && l.etat === VIVANT && (
                  <p className="mention">
                    {etat.peutEmettre
                      ? "Adresse publique non configurée : le QR n'est pas affichable."
                      : "Votre rôle permet de lire l'avancement, pas de faire signer : le lien ne vous est pas rendu."}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
